import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Button, Tag, Space, Alert, Card, Statistic, Row, Col, Modal } from 'antd';
import { PlusOutlined, EditOutlined, StopOutlined } from '@ant-design/icons';
import { Auth } from 'aws-amplify';
import { getUsersByVendorGroup, getActiveLicenseCount, getUserInfo, getPendingLicenseApprovals } from '../../graphql/queries';
import { deactivateUserBySupplierAdmin } from '../../graphql/mutations';
import { axiosRequest } from '../../utils/axiosRequest';
import styles from './SupplierAdminPortal.module.css';

const getHeaders = async () => {
  const session = await Auth.currentSession();
  const idToken = session.getIdToken();
  const jwt = idToken.getJwtToken();
  return {
    'Content-Type': 'application/json',
    'Access-Control-Allow-Origin': '*',
    Authorization: jwt,
  };
};

export const SupplierAdminPortal = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [licenseInfo, setLicenseInfo] = useState({ activeCount: 0, isStandardProgram: false });
  const [pendingApprovalsCount, setPendingApprovalsCount] = useState(0);
  const [vendorGroupNumber, setVendorGroupNumber] = useState(null);
  const [vendorGroupName, setVendorGroupName] = useState('');

  useEffect(() => {
    // Get user info from database
    Auth.currentSession()
      .then(async (session) => {
        const userEmail = session.getIdToken().payload.email;
        const headers = {
          'Content-Type': 'application/json',
          'Access-Control-Allow-Origin': '*',
          Authorization: session.getIdToken().getJwtToken(),
        };

        const body = {
          query: getUserInfo,
          variables: { mail: userEmail },
        };

        const result = await axiosRequest({ headers, body, method: 'post' });
        const userData = result.data?.data?.getUserInfo?.data?.[0];

        if (userData) {
          setVendorGroupNumber(userData.vendorGroupNumber);
          setVendorGroupName(userData.vendorName || '');
        }
      })
      .catch((err) => {
        console.error('Error getting user info:', err);
      });
  }, []);

  const fetchData = useCallback(async () => {
    if (!vendorGroupNumber) return;

    setLoading(true);
    try {
      const headers = await getHeaders();

      // Fetch users
      const usersBody = {
        query: getUsersByVendorGroup,
        variables: { vendorGroupNumber: parseInt(vendorGroupNumber) },
      };
      const usersResult = await axiosRequest({ headers, body: usersBody, method: 'post' });
      setUsers(usersResult.data?.data?.getUsersByVendorGroup?.data || []);

      // Fetch license count
      const licenseBody = {
        query: getActiveLicenseCount,
        variables: { vendorGroupNumber: parseInt(vendorGroupNumber) },
      };
      const licenseResult = await axiosRequest({ headers, body: licenseBody, method: 'post' });
      setLicenseInfo(licenseResult.data?.data?.getActiveLicenseCount || { activeCount: 0, isStandardProgram: false });

      // Fetch pending approvals
      const pendingApprovalsBody = {
        query: getPendingLicenseApprovals,
        variables: { vendorGroupNumber: parseInt(vendorGroupNumber) },
      };
      const pendingApprovalsResult = await axiosRequest({ headers, body: pendingApprovalsBody, method: 'post' });
      setPendingApprovalsCount(pendingApprovalsResult.data?.data?.getPendingLicenseApprovals?.rowsReturned || 0);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [vendorGroupNumber]);

  useEffect(() => {
    if (vendorGroupNumber) {
      fetchData();
    }
  }, [vendorGroupNumber, fetchData]);

  const handleCreateUser = () => {
    navigate('/supplier-admin/create-user');
  };

  const handleDeactivateUser = async (email) => {
    Modal.confirm({
      title: 'Deactivate User',
      content: 'Are you sure you want to deactivate this user? This will immediately revoke their access.',
      okText: 'Deactivate',
      okType: 'danger',
      onOk: async () => {
        try {
          const headers = await getHeaders();
          const body = {
            query: deactivateUserBySupplierAdmin,
            variables: { email },
          };
          await axiosRequest({ headers, body, method: 'post' });
          fetchData(); // Refresh list
        } catch (error) {
          console.error('Error deactivating user:', error);
        }
      }
    });
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'firstName',
      key: 'name',
      render: (text, record) => `${record.firstName} ${record.lastName}`,
      sorter: (a, b) => a.firstName.localeCompare(b.firstName),
    },
    {
      title: 'Email',
      dataIndex: 'mail',
      key: 'mail',
      sorter: (a, b) => a.mail.localeCompare(b.mail),
    },
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'active',
      render: (active) => (
        <Tag color={active ? 'green' : 'red'}>
          {active ? 'Active' : 'Inactive'}
        </Tag>
      ),
      filters: [
        { text: 'Active', value: true },
        { text: 'Inactive', value: false },
      ],
      onFilter: (value, record) => record.active === value,
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => new Date(date).toLocaleDateString(),
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => navigate('/supplier-admin/edit-user', { state: { user: record } })}
          >
            Edit
          </Button>
          {record.active && (
            <Button
              type="link"
              danger
              icon={<StopOutlined />}
              onClick={() => handleDeactivateUser(record.mail)}
            >
              Deactivate
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className={styles['supplier-admin-portal']}>
      <h1>User Management Portal</h1>
      <p className={styles.subtitle}>{vendorGroupName}</p>

      {licenseInfo.isStandardProgram && (
        <Alert
          message="Standard Supplier Program"
          description="Your organization is on the Standard Supplier Program with unlimited user licenses."
          type="info"
          showIcon
          className={styles['mb-16']}
        />
      )}

      <Row gutter={16} className={styles['mb-24']}>
        <Col span={8}>
          <Card>
            <Statistic
              title="Active Licenses"
              value={licenseInfo.activeCount}
              suffix={licenseInfo.isStandardProgram ? '/ Unlimited' : ''}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Total Users"
              value={users.length}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Pending Approvals"
              value={pendingApprovalsCount}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      <div className={styles['mb-16']}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreateUser}
        >
          Add New User
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={users}
        loading={loading}
        rowKey="mail"
        pagination={{ pageSize: 10 }}
      />
    </div>
  );
};
